import { Modal, NumberInput, SegmentedControl, Switch, TagsInput, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type {
    CreateRetentionPolicyRequest,
    RetentionPolicyType,
} from '@xver/apis/v1/sammeln/retention/model';
import Button from '@xver/components/Atoms/Button/Button';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RETENTION_POLICIES_QUERY_KEY } from '../hooks/useRetentionPoliciesQuery';

interface AddRetentionPolicyModalProps {
    opened: boolean;
    onClose: () => void;
    projectIdOrName: string;
}

export default function AddRetentionPolicyModal({
    opened,
    onClose,
    projectIdOrName,
}: AddRetentionPolicyModalProps) {
    const { t } = useTranslation('artifactRegistry');
    const queryClient = useQueryClient();
    const typeLabelId = useId();

    const [repositoryName, setRepositoryName] = useState('');
    const [type, setType] = useState<RetentionPolicyType>('count');
    const [maxCount, setMaxCount] = useState<number | string>(10);
    const [maxAgeDays, setMaxAgeDays] = useState<number | string>(30);
    const [keepPatterns, setKeepPatterns] = useState<string[]>([]);
    const [keepLatestOnly, setKeepLatestOnly] = useState(false);
    const [enabled, setEnabled] = useState(true);

    useEffect(() => {
        if (!opened) return;
        setRepositoryName('');
        setType('count');
        setMaxCount(10);
        setMaxAgeDays(30);
        setKeepPatterns([]);
        setKeepLatestOnly(false);
        setEnabled(true);
    }, [opened]);

    const createMutation = useMutation({
        mutationFn: () => {
            const payload: CreateRetentionPolicyRequest = {
                repository_name: repositoryName.trim() ? repositoryName.trim() : undefined,
                type,
                max_count:
                    type === 'count'
                        ? typeof maxCount === 'number'
                            ? maxCount
                            : Number(maxCount)
                        : undefined,
                max_age_days:
                    type === 'age'
                        ? typeof maxAgeDays === 'number'
                            ? maxAgeDays
                            : Number(maxAgeDays)
                        : undefined,
                keep_patterns: keepPatterns.length > 0 ? keepPatterns : undefined,
                keep_latest_only: keepLatestOnly,
                enabled,
            };
            return Endpoints.v1.Sammeln.Retention.createRetentionPolicy(projectIdOrName, payload);
        },
        onSuccess: async () => {
            notifications.show({
                title: t('retention.toast.createTitle', 'Policy created'),
                message: t(
                    'retention.toast.createMessage',
                    'The retention policy will be applied on the next cleanup cycle.'
                ),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await queryClient.invalidateQueries({
                queryKey: [RETENTION_POLICIES_QUERY_KEY, projectIdOrName],
            });
            onClose();
        },
        onError: (err: { message?: string }) => {
            notifications.show({
                title: t('retention.toast.errorTitle', 'Action failed'),
                message:
                    err?.message ?? t('retention.toast.errorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const handleSubmit = () => {
        if (createMutation.isPending) return;
        if (type === 'count' && Number(maxCount) <= 0) return;
        if (type === 'age' && Number(maxAgeDays) <= 0) return;
        createMutation.mutate();
    };

    return (
        <Modal
            size="md"
            opened={opened}
            onClose={onClose}
            withCloseButton={false}
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <Modal.Title>
                        <span className="text-base font-bold">
                            {t('retention.modal.title', 'Create retention policy')}
                        </span>
                    </Modal.Title>
                    <p className="text-sm text-fg/68">
                        {t(
                            'retention.modal.description',
                            'Automatically remove artifacts that match this rule.'
                        )}
                    </p>
                </div>

                <TextInput
                    label={t('retention.modal.repository', 'Repository name')}
                    value={repositoryName}
                    onChange={(e) => setRepositoryName(e.currentTarget.value)}
                    placeholder={t(
                        'retention.modal.repositoryPlaceholder',
                        'Leave blank to apply to all repositories'
                    )}
                    description={t(
                        'retention.modal.repositoryHint',
                        'If empty, the policy applies to all repositories in the project.'
                    )}
                />

                <div className="flex flex-col gap-1">
                    <span id={typeLabelId} className="text-sm font-medium">
                        {t('retention.modal.type', 'Policy type')}
                    </span>
                    <SegmentedControl
                        aria-labelledby={typeLabelId}
                        size="sm"
                        radius="md"
                        styles={{
                            root: {
                                backgroundColor: 'var(--surface-2)',
                                border: '0.5px solid var(--line)',
                            },
                        }}
                        value={type}
                        onChange={(value) => setType(value as RetentionPolicyType)}
                        withItemsBorders={false}
                        data={[
                            { value: 'count', label: t('retention.modal.typeCount', 'Count') },
                            { value: 'age', label: t('retention.modal.typeAge', 'Age') },
                            {
                                value: 'untagged',
                                label: t('retention.modal.typeUntagged', 'Untagged'),
                            },
                        ]}
                    />
                </div>

                {type === 'count' && (
                    <NumberInput
                        label={t('retention.modal.maxCount', 'Max artifacts to keep')}
                        value={maxCount}
                        onChange={setMaxCount}
                        min={1}
                        step={1}
                        description={t(
                            'retention.modal.maxCountHint',
                            'Only the most recent N artifacts are retained.'
                        )}
                    />
                )}

                {type === 'age' && (
                    <NumberInput
                        label={t('retention.modal.maxAge', 'Max age in days')}
                        value={maxAgeDays}
                        onChange={setMaxAgeDays}
                        min={1}
                        step={1}
                        description={t(
                            'retention.modal.maxAgeHint',
                            'Artifacts older than this are removed.'
                        )}
                    />
                )}

                <TagsInput
                    label={t('retention.modal.keepPatterns', 'Tag patterns to keep')}
                    value={keepPatterns}
                    onChange={setKeepPatterns}
                    placeholder={t('retention.modal.keepPatternsPlaceholder', 'e.g. v*, release-*')}
                    description={t(
                        'retention.modal.keepPatternsHint',
                        'Artifacts whose tag matches one of these patterns are always kept.'
                    )}
                    clearable
                />

                <Switch
                    label={t('retention.modal.keepLatest', 'Always keep the latest artifact')}
                    checked={keepLatestOnly}
                    onChange={(e) => setKeepLatestOnly(e.currentTarget.checked)}
                />

                <Switch
                    label={t('retention.modal.enabled', 'Enabled')}
                    checked={enabled}
                    onChange={(e) => setEnabled(e.currentTarget.checked)}
                />

                <div className="w-full flex flex-row justify-end items-center gap-2 pt-2 border-t-[0.5px] border-line">
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        disabled={createMutation.isPending}
                    >
                        {t('retention.modal.cancel', 'Cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={createMutation.isPending}
                        isLoading={createMutation.isPending}
                    >
                        {t('retention.modal.submit', 'Create policy')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
